import { useEffect, useState } from "react";
import {
    CircularProgress,
    Dialog,
    FormControlLabel,
    FormGroup,
    Checkbox,
    DialogContent,
    DialogActions,
    Button,
    Typography,
} from "@material-ui/core";
import { Link } from "react-router-dom";
import {
    useSharedLists,
    addMovieToSharedList,
    removeMovieFromSharedList,
} from "../api/firebase";
import { useAuth } from "../context/auth";

const db = window.firebase.firestore();

const SharedListSelectionDialog = (props) => {
    const { movie, open, onClose } = props;

    const authState = useAuth();
    const sharedLists = useSharedLists();

    const [addStates, setAddStates] = useState(new Map());

    const onStateChanged = (sharedList, state) => {
        const clone = new Map(addStates);
        clone.set(sharedList, state);
        setAddStates(clone);
    };

    const [applying, setApplying] = useState(false);
    const onApply = async () => {
        setApplying(true);

        const promises = [];
        for (const [sharedList, state] of addStates) {
            if (!state.wasAdded && state.isAdded) {
                promises.push(
                    addMovieToSharedList(sharedList, movie, authState.user.uid)
                );
            } else if (state.wasAdded && !state.isAdded) {
                promises.push(
                    removeMovieFromSharedList(
                        sharedList,
                        movie,
                        authState.user.uid
                    )
                );
            }
        }

        if (promises.length === 0) {
            onClose(null);
            return;
        }

        try {
            await Promise.all(promises);
            onClose({});
        } catch (e) {
            onClose({ error: "Error applying changes." });
        }
    };

    const render = () => {
        if (authState) {
            if (authState.isAuthenticated) {
                if (sharedLists) {
                    return sharedLists.map((sharedList) => (
                        <SharedList
                            key={sharedList.id}
                            sharedList={sharedList}
                            traktId={movie.ids.trakt}
                            applying={applying}
                            onStateChanged={onStateChanged}
                        />
                    ));
                } else {
                    return (
                        <div style={{ margin: "auto auto" }}>
                            <CircularProgress color="secondary" />
                        </div>
                    );
                }
            } else {
                return (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                        }}
                    >
                        <Typography variant="body1">
                            Login to view your shared lists.
                        </Typography>
                        <Link to="/login">
                            <Button style={{ marginTop: "25px" }}>Login</Button>
                        </Link>
                    </div>
                );
            }
        } else {
            return (
                <div style={{ margin: "auto auto" }}>
                    <CircularProgress color="secondary" />
                </div>
            );
        }
    };

    return (
        <Dialog open={open} onClose={() => onClose(null)}>
            <DialogContent>
                <div style={{ display: "flex", flexDirection: "column" }}>
                    {render()}
                </div>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose(null)} disabled={applying}>
                    Cancel
                </Button>
                <Button
                    onClick={onApply}
                    disabled={
                        !(
                            !applying &&
                            authState &&
                            authState.isAuthenticated
                        )
                    }
                >
                    Apply
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const SharedList = (props) => {
    const { sharedList, traktId, applying, onStateChanged } = props;

    const [checked, setChecked] = useState(false);
    const { added } = useIsMovieInSharedList(sharedList.id, traktId);

    useEffect(() => {
        setChecked(added);
    }, [added]);

    return (
        <FormGroup>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={checked}
                        disabled={applying}
                        onChange={(event) => {
                            setChecked(event.target.checked);
                            onStateChanged(sharedList, {
                                wasAdded: added,
                                isAdded: event.target.checked,
                            });
                        }}
                    />
                }
                label={sharedList.name}
            />
        </FormGroup>
    );
};

const useIsMovieInSharedList = (sharedListId, traktId) => {
    const [added, setAdded] = useState(false);

    useEffect(() => {
        const unsubscribe = db
            .collection("sharedLists")
            .doc(sharedListId)
            .collection("items")
            .where("traktId", "==", traktId)
            .onSnapshot((querySnapshot) => {
                const docs = [];
                querySnapshot.forEach((doc) =>
                    docs.push({ id: doc.id, ...doc.data() })
                );
                setAdded(docs.length > 0);
            });
        return () => unsubscribe();
    }, [sharedListId, traktId]);

    return { added };
};

export default SharedListSelectionDialog;
